import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { ActivatedRoute, Router } from '@angular/router';
import { Observable, forkJoin } from 'rxjs';
import { CustomerVisit } from 'src/app/core/interfaces/customer-visit';
import { ChangeOrder } from 'src/app/core/interfaces/production/change-order';
import { ApiService } from 'src/app/core/services/api/api.service';
import { IdbService } from 'src/app/core/services/idb/idb.service';
import { RoleService } from 'src/app/core/services/role/role.service';

@Component({
  selector: 'app-customer-visit-counts-list',
  templateUrl: './customer-visit-counts-list.component.html',
  styleUrls: ['./customer-visit-counts-list.component.scss'],
})
export class CustomerVisitCountsListComponent implements OnInit {
  @Input() status: string;
  statusMessage: string;
  statusNumber: number;

  customerVisitDisplayedColumns: string[] = [
    'customerVisitId',
    'assignedName',
    'estimator',
    'createdDate',
    'statusName',
    'customerName',
    'jobAddress',
    'dispositionName',
    'subDispositionName',
    'dispositionNotes',
    'dispositionDate',
  ];

  availableForInsideSalesDisplayedColumns: string[] = [
    'createdDate',
    'customerName',
    'jobAddress',
    'discountedTotal',
    'estimator',
    'dispositionDate',
  ];

  changeOrderDisplayedColumns: string[] = [
    'projectid',
    'customerId',
    'jobAddressId',
    'discountedTotal',
    'assignedName',
    'createdDate',
    'description',
  ];

  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;

  dataSourceArray: CustomerVisit[] | ChangeOrder[] = [];
  customerVisitDataSource: MatTableDataSource<CustomerVisit>;
  changeOrderDataSource: MatTableDataSource<ChangeOrder>;

  isLoading: boolean = true;

  constructor(
    private apiService: ApiService,
    private router: Router,
    private route: ActivatedRoute,
    private role: RoleService,
    private idb: IdbService
  ) {}

  ngOnInit(): void {
    this.isLoading = true;
    this.customerVisitDataSource = new MatTableDataSource();
    this.changeOrderDataSource = new MatTableDataSource();
    this.route.paramMap.subscribe((params) => {
      this.status = params.get('status') as string;
      this.getDataSource(this.status);
    });
  }

  getDataSource(status: string) {
    this.customerVisitDataSource = new MatTableDataSource();
    this.changeOrderDataSource = new MatTableDataSource();
    this.isLoading = true;

    if (status) {
      switch (status) {
        case 'customerVisitsNeedingDisposition':
          this.statusNumber = 3; // ?
          this.statusMessage = 'Customer Visits needing Disposition';
          break;
        case 'pendingCustomerVisits':
          this.statusNumber = 6;
          this.statusMessage = 'Pending Customer Visits';
          break;
        case 'availableForInsideSales':
          this.statusNumber = 7;
          this.statusMessage = 'Customer Visits available for Inside Sales';
          break;
        case 'hotQuotes':
          this.statusNumber = 8;
          this.statusMessage = 'Hot Quotes';
          break;
        case 'pendingDeadAppointments':
          this.statusNumber = 9;
          this.statusMessage = 'Pending Dead Appointments/Customer Visits';
          break;
        case 'pendingChangeOrders':
          this.statusNumber = 20;
          this.statusMessage = 'Pending Change Orders';
          break;
      }
    }

    if (this.statusNumber != undefined) {
      if (this.statusNumber === 20) {
        this.apiService.getPendingChangeOrders().subscribe({
          next: (response) => {
            this.isLoading = false;
            this.dataSourceArray = response;
            this.changeOrderDataSource = new MatTableDataSource(
              this.dataSourceArray
            );
            this.changeOrderDataSource.sort = this.sort;
            this.changeOrderDataSource.paginator = this.paginator;
          },
          error: (e) => {
            this.isLoading = false;
            console.error(e);
          },
        });
      } else if (this.statusNumber === 3) {
        if (this.role.getCurrentOfflineStatus()) {
          const pendingStatusValues = [1, 3];

          const pendingQueries: Observable<CustomerVisit[]>[] =
            pendingStatusValues.map((status) => {
              return this.idb.getCustomerVisitsByStatus(status);
            });

          forkJoin(pendingQueries).subscribe({
            next: (response) => {
              console.log(response);
              this.isLoading = false;
              this.dataSourceArray = response.flat();
              this.customerVisitDataSource = new MatTableDataSource(
                this.dataSourceArray
              );
              this.customerVisitDataSource.sort = this.sort;
              this.customerVisitDataSource.paginator = this.paginator;
            },
            error: (e) => {
              this.isLoading = false;
              console.error(e);
            },
          });
        } else {
          this.apiService
            .getCustomerVisitByStatus(this.statusNumber)
            .subscribe({
              next: (response) => {
                this.isLoading = false;
                this.dataSourceArray = response;
                this.customerVisitDataSource = new MatTableDataSource(
                  this.dataSourceArray
                );
                this.customerVisitDataSource.sort = this.sort;
                this.customerVisitDataSource.paginator = this.paginator;
              },
              error: (e) => {
                this.isLoading = false;
                console.error(e);
              },
            });
        }
      } else if (this.statusNumber === 7) {
        // Inside Sales
        this.apiService.getCustomerVisitByStatus(this.statusNumber).subscribe({
          next: (response) => {
            this.isLoading = false;
            this.dataSourceArray = response;
            this.customerVisitDataSource = new MatTableDataSource(
              this.dataSourceArray
            );
            this.customerVisitDataSource.sort = this.sort;
            this.customerVisitDataSource.paginator = this.paginator;
          },
          error: (e) => {
            this.isLoading = false;
            console.error(e);
          },
        });
      } else {
        if (this.role.getCurrentOfflineStatus()) {
          this.idb.getCustomerVisitsByStatus(this.statusNumber).subscribe({
            next: (response) => {
              console.log(response);
              this.isLoading = false;
              this.dataSourceArray = response;
              this.customerVisitDataSource = new MatTableDataSource(
                this.dataSourceArray
              );
              this.customerVisitDataSource.sort = this.sort;
              this.customerVisitDataSource.paginator = this.paginator;
            },
            error: (e) => {
              this.isLoading = false;
              console.error(e);
            },
          });
        } else {
          this.apiService
            .getCustomerVisitByStatus(this.statusNumber)
            .subscribe({
              next: (response) => {
                console.log('is it this?', response);
                this.isLoading = false;
                this.dataSourceArray = response;
                this.customerVisitDataSource = new MatTableDataSource(
                  this.dataSourceArray
                );
                this.customerVisitDataSource.sort = this.sort;
                this.customerVisitDataSource.paginator = this.paginator;
              },
              error: (e) => {
                this.isLoading = false;
                console.error(e);
              },
            });
        }
      }
    }
  }

  goToCustomerVisit(row: CustomerVisit) {
    if (this.role.getCurrentOfflineStatus()) {
      this.router.navigate(['customers/' + row.customerId], {
        queryParams: {
          jobAddressId: row.jobAddressId,
          appointmentId: row.appointmentId,
        },
      });
    } else {
      if (this.statusNumber === 9) {
        this.router.navigate(['customers/' + row.customerId], {
          queryParams: {
            jobAddressId: row.jobAddressId,
            customerVisitId: row.customerVisitId,
            pendingDeadAppointment: true,
          },
        });
      } else if (this.statusNumber === 7) {
        this.router.navigate(['customers/' + row.customerId], {
          queryParams: {
            jobAddressId: row.jobAddressId,
            customerVisitId: row.customerVisitId,
            insideSales: true,
          },
        });
      } else {
        this.router.navigate(['customers/' + row.customerId], {
          queryParams: {
            jobAddressId: row.jobAddressId,
            customerVisitId: row.customerVisitId,
          },
        });
      }
    }
  }

  goToChangeOrder(row: ChangeOrder) {
    // Go to project info
    this.router.navigate(['customers/' + row.customerId], {
      queryParams: {
        jobAddressId: row.jobAddressId,
        projectId: row.projectid,
        projectInfo: true,
      },
    });
  }

  filterCustomerVisits(event: Event) {
    const filterString = (event.target as HTMLInputElement).value;
    this.customerVisitDataSource.filter = filterString.trim().toLowerCase();
  }

  filterChangeOrders(event: Event) {
    const filterString = (event.target as HTMLInputElement).value;
    this.changeOrderDataSource.filter = filterString.trim().toLowerCase();
  }
}
